import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import {
  InvitationsService,
  IReminderResponse,
  IReminderResult,
  IUpdateInvitationContactMethodPayload
} from 'core/services/invitations.service';
import { ReplaySubject, catchError, finalize, map, Observable, of, switchMap, takeUntil, tap, throwError } from 'rxjs';
import { InvitationTemplate } from 'core/types/invitation.interface';
import { NetworkError } from 'shared/models/network-error';
import { IReferral, Reminder } from 'core/types/referral.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';
import { User } from 'shared/types/user.model';
import { AuthService } from 'core/services/auth.service';
import { TemplatesService } from 'core/services/templates.service';
import { CustomMessageService } from 'core/services/custom-message.service';
import { FailedReminderModalComponent } from './failed-reminder-modal/failed-reminder-modal.component';
import { IPagination } from 'shared/types/pagination.interface';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { KorviuInputComponent } from '../../../../shared/components/korviu-input/korviu-input.component';
import { KorviuTextareaComponent } from 'shared/components/korviu-textarea/korviu-textarea.component';
import {
  buildWhatsappSharePayload,
  ensureWhatsappShareLogged,
  getWhatsappMessageFromUrl,
  withWhatsappMessage
} from 'shared/utils/whatsapp-share.utils';
import { resolveReferralPhoneParts } from 'shared/utils/referral-contact.utils';
import {
  buildReferralSignupDeepLink,
  replaceReferralTemplatePlaceholders
} from 'shared/utils/referral-template-placeholders.utils';
import { MAX_INVITATION_REMINDERS } from 'core/types/invitation-contact.type';

const HANDLED_INVITATION_ERROR_CODES = new Set([
  'ERR_MAX_REMINDERS',
  'ERR_BULK_DISABLED',
  'ERR_EMAIL_REGISTERED',
  'ERR_PHONE_REGISTERED',
  'ERR_DUPLICATE'
]);

@Component({
  selector: 'app-send-reminder-modal',
  templateUrl: './send-reminder-modal.component.html',
  styleUrl: './send-reminder-modal.component.scss',
  standalone: true,
  imports: [
    NzTagModule,
    TranslateModule,
    NzSkeletonModule,
    KorviuButtonComponent,
    NzModalModule,
    ReactiveFormsModule,
    NzRadioModule,
    KorviuInputComponent,
    KorviuTextareaComponent
  ]
})
export class SendReminderModalComponent implements OnInit, OnDestroy {
  @Output() public reminderSent: EventEmitter<void> = new EventEmitter<void>();
  public isLoading: boolean;
  public reminders: Reminder[];
  public messageTemplate: { [key: string]: InvitationTemplate[] };
  public selectedMessage: string;
  public referralName: string;
  public pagination: IPagination;
  public selectedLanguage: string;
  public contactForm: FormGroup;

  public get contactMethodControl(): FormControl {
    return this.contactForm?.get('type') as FormControl;
  }

  public get phoneNumberPrefixControl(): FormControl {
    return this.contactForm?.get('country_code') as FormControl;
  }

  public get phoneNumberControl(): FormControl {
    return this.contactForm?.get('phone_number') as FormControl;
  }

  public get emailControl(): FormControl {
    return this.contactForm?.get('email') as FormControl;
  }

  public get messageLanguageControl(): FormControl {
    return this.contactForm?.get('language') as FormControl;
  }

  public get messageControl(): FormControl {
    return this.contactForm?.get('message') as FormControl;
  }

  public get isSendDisabled(): boolean {
    return this.isLoading || !this.contactForm?.valid || this.reminderCount >= MAX_INVITATION_REMINDERS;
  }

  public get reminderCount(): number {
    return this.modalData.referralInfo.total_reminders ?? this.reminders.length;
  }

  public get currentUser(): User {
    return this._authService.currentUser;
  }
  private readonly _onDestroy$: ReplaySubject<void>;

  constructor(
    @Inject(NZ_MODAL_DATA) public modalData: { referralInfo: IReferral },
    private _modal: NzModalRef,
    private _templatesService: TemplatesService,
    private _modalService: NzModalService,
    private _authService: AuthService,
    private _messageService: CustomMessageService,
    private _invitationService: InvitationsService,
    private _translateService: TranslateService
  ) {
    this.messageTemplate = {};
    this.isLoading = false;
    this.referralName = '';
    this.pagination = { page: 1, pageSize: 10 };
    this.reminders = this.modalData.referralInfo.reminders;
    this.selectedLanguage = this._normalizeMessageLanguage(this._translateService.currentLang);
    const invitationType = this._normalizeContactMethodType(
      this.modalData?.referralInfo?.invitation_type?.name ||
        this.modalData?.referralInfo?.type ||
        this.modalData?.referralInfo?.contact_method
    );

    this.contactForm = new FormGroup({
      type: new FormControl(invitationType || 'whatsapp'),
      phone_number: new FormControl(this._getPhoneNumber()),
      country_code: new FormControl(this._getCountryCode()),
      email: new FormControl(this.modalData?.referralInfo?.email || ''),
      language: new FormControl(this.selectedLanguage),
      message: new FormControl('')
    });

    this._onDestroy$ = new ReplaySubject<void>(1);
  }

  public ngOnInit(): void {
    this._setDefaultValidators();
    this._setContactFormListeners();
    this._getInvitationMessages();
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public onPhoneNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9]/g, '');
    this.phoneNumberControl.setValue(input.value);
  }

  public send(): void {
    this._setDefaultValidators();
    this.contactForm.markAllAsTouched();

    if (this.contactForm.invalid || this.reminderCount >= MAX_INVITATION_REMINDERS) {
      return;
    }

    this.isLoading = true;
    const referralId = this.modalData.referralInfo.id;
    const contactMethodPayload = this._buildContactMethodPayload();
    const updateContactMethod$ =
      !!referralId && !!contactMethodPayload
        ? this._invitationService.updateInvitationContactMethod(referralId, contactMethodPayload)
        : of(null);

    updateContactMethod$
      .pipe(
        switchMap(() => this._invitationService.sendReminder(referralId, this.selectedLanguage)),
        switchMap((response: IReminderResponse) => this._processReminderResponse(response)),
        tap((isProcessed: boolean) => {
          if (!isProcessed) {
            return;
          }

          this._showSuccessAndClose();
        }),
        finalize(() => {
          this.isLoading = false;
        }),
        catchError((error: NetworkError) => {
          if (!HANDLED_INVITATION_ERROR_CODES.has(error?.errorPayload?.messageCode)) {
            this._openSendErrorReminderModal();
          }
          return of(false);
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe();
  }

  public cancel(): void {
    this._modal.destroy();
  }

  private _getInvitationMessages(options: { preserveManualMessage?: boolean } = {}): void {
    this.isLoading = true;
    const preserveManualMessage = options.preserveManualMessage && this._hasManualMessageEdit();

    const stageName: string = (this.modalData?.referralInfo?.status?.name || '').toLowerCase();
    const liveInUS: string = this.modalData?.referralInfo?.live_in_us ? 'us' : 'mx';
    const filters = {
      name: stageName,
      language: this.selectedLanguage,
      location: liveInUS,
      contactMethod: this.contactMethodControl?.value
    };

    this._templatesService
      .getReferralTemplate(this.pagination, filters, this.contactMethodControl?.value)
      .pipe(
        tap((response: IGetPaginatedResponse<InvitationTemplate[]>) => {
          this.messageTemplate[stageName] = response.results;
          this._selectMessage({ preserveManualMessage });
        }),
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe();
  }

  private _selectMessage(options: { preserveManualMessage?: boolean } = {}): void {
    const stageName: string = (this.modalData?.referralInfo?.status?.name || '').toLowerCase();
    const reminderCount = this.reminderCount;
    const languageCode: string = this.selectedLanguage;
    let templatePrefix: string;
    if (reminderCount === 0) {
      templatePrefix = `${stageName}_reminder_1_${languageCode}`;
    } else if (reminderCount === 1) {
      templatePrefix = `${stageName}_reminder_2_${languageCode}`;
    } else if (reminderCount === 2) {
      templatePrefix = `${stageName}_reminder_3_${languageCode}`;
    } else {
      templatePrefix = `${stageName}_reminder_1_${languageCode}`;
    }

    const templates: InvitationTemplate[] = this.messageTemplate[stageName] || [];
    const selectedTemplate: InvitationTemplate = templates.find(
      (template: InvitationTemplate) =>
        template.name.includes(templatePrefix) && template.language.code === this.selectedLanguage
    );

    if (selectedTemplate) {
      this.selectedMessage = this._replacePlaceholders(
        selectedTemplate.body,
        this.modalData.referralInfo,
        this.currentUser as User
      );
    } else if (templates.length > 0) {
      this.selectedMessage = this._replacePlaceholders(
        templates[0].body,
        this.modalData.referralInfo,
        this.currentUser as User
      );
    } else {
      this.selectedMessage = '';
    }

    if (options.preserveManualMessage) {
      return;
    }

    this.messageControl.setValue(this.selectedMessage);
    this.messageControl.markAsPristine();
  }

  private _openSendErrorReminderModal(): void {
    this._modalService.create({
      nzContent: FailedReminderModalComponent,
      nzCentered: true,
      nzWidth: 554,
      nzWrapClassName: 'modal-wrap'
    });
  }

  private _setContactFormListeners(): void {
    this.contactMethodControl.valueChanges.pipe(takeUntil(this._onDestroy$)).subscribe(() => {
      this._setDefaultValidators();
      this._getInvitationMessages({ preserveManualMessage: true });
    });

    this.messageLanguageControl.valueChanges.pipe(takeUntil(this._onDestroy$)).subscribe((language: string) => {
      this.selectedLanguage = this._normalizeMessageLanguage(language);
      this._getInvitationMessages();
    });
  }

  private _setDefaultValidators(): void {
    this.contactMethodControl.setValidators([Validators.required]);
    this.messageLanguageControl.setValidators([Validators.required]);
    this.messageControl.setValidators([Validators.required]);
    this.phoneNumberControl.clearValidators();
    this.phoneNumberPrefixControl.clearValidators();
    this.emailControl.clearValidators();

    if (this.contactMethodControl.value === 'email') {
      this.emailControl.setValidators([Validators.required, Validators.email]);
    } else {
      this.phoneNumberControl.setValidators([
        Validators.required,
        Validators.pattern('^[0-9]*$'),
        Validators.minLength(10)
      ]);
      this.phoneNumberPrefixControl.setValidators([Validators.required]);
    }

    this.contactMethodControl.updateValueAndValidity({ emitEvent: false });
    this.messageLanguageControl.updateValueAndValidity({ emitEvent: false });
    this.messageControl.updateValueAndValidity({ emitEvent: false });
    this.phoneNumberControl.updateValueAndValidity({ emitEvent: false });
    this.phoneNumberPrefixControl.updateValueAndValidity({ emitEvent: false });
    this.emailControl.updateValueAndValidity({ emitEvent: false });
  }

  private _buildContactMethodPayload(): IUpdateInvitationContactMethodPayload | null {
    const contactType = this._normalizeContactMethodType(this.contactMethodControl.value);

    if (!contactType) {
      return null;
    }

    if (contactType === 'email') {
      return {
        type: 'email',
        email: this.emailControl.value?.trim()
      };
    }

    return {
      type: contactType,
      phone_number: this.phoneNumberControl.value?.trim(),
      country_code: this.phoneNumberPrefixControl.value?.trim()
    };
  }

  private _normalizeContactMethodType(type?: string): IUpdateInvitationContactMethodPayload['type'] | null {
    const normalizedType = type?.toLowerCase();

    if (normalizedType === 'email') {
      return 'email';
    }

    if (normalizedType === 'whatsapp') {
      return 'whatsapp';
    }

    if (normalizedType === 'sms' || normalizedType === 'phone') {
      return 'whatsapp';
    }

    return null;
  }

  private _normalizeMessageLanguage(language?: string): string {
    return language === 'en' ? 'en' : 'es';
  }

  private _hasManualMessageEdit(): boolean {
    return this.messageControl.dirty && this.messageControl.value !== this.selectedMessage;
  }

  private _getCountryCode(): string {
    return this._getPhoneParts().countryCode;
  }

  private _getPhoneNumber(): string {
    return this._getPhoneParts().phoneNumber;
  }

  private _getPhoneParts(): { countryCode: string; phoneNumber: string } {
    return resolveReferralPhoneParts({
      value: this.modalData?.referralInfo?.value,
      countryCode: this.modalData?.referralInfo?.country_code,
      phoneNumber: this.modalData?.referralInfo?.phone_number,
      localPhoneNumber: this.modalData?.referralInfo?.local_phone_number,
      liveInUs: this.modalData?.referralInfo?.live_in_us
    });
  }

  private _replacePlaceholders(template: string, referralInfo: IReferral, user: User): string {
    const referralCode = user.referralCode;

    return replaceReferralTemplatePlaceholders(template, {
      referralFirstName: referralInfo.first_name,
      referringMemberName: user.firstName,
      referralCode,
      signupDeepLink: buildReferralSignupDeepLink(window.location.origin, referralCode)
    });
  }

  private _processReminderResponse(response: IReminderResponse): Observable<boolean> {
    const result = this._getCurrentReminderResult(response);

    if (result && HANDLED_INVITATION_ERROR_CODES.has(result.messageCode)) {
      this._messageService.error(this._translateService.instant(`errors.${result.messageCode}`));
      return of(false);
    }

    if (!result || result.messageCode !== 'SUCCESS') {
      this._openSendErrorReminderModal();
      return of(false);
    }

    if (!result.whatsapp_url) {
      return of(true);
    }

    const message =
      this.messageControl.value?.trim() || getWhatsappMessageFromUrl(result.whatsapp_url) || result.message || '';
    const whatsappUrl = withWhatsappMessage(result.whatsapp_url, message);
    window.open(whatsappUrl, '_blank');

    if (!message) {
      return throwError(() => new Error('WhatsApp share message is missing'));
    }

    return this._invitationService
      .logWhatsappShare(result.invitation_id, buildWhatsappSharePayload(result.invitation_id, message))
      .pipe(
        map(response => {
          ensureWhatsappShareLogged(response);
          return true;
        })
      );
  }

  private _getCurrentReminderResult(response: IReminderResponse): IReminderResult | null {
    const results = response?.results || [];
    return (
      results.find((result: IReminderResult) => result.invitation_id === this.modalData.referralInfo.id) ||
      results[0] ||
      null
    );
  }

  private _showSuccessAndClose(): void {
    this.referralName = `${this.modalData.referralInfo.first_name} ${this.modalData.referralInfo.last_name}`;
    const translatedText: string = this._translateService.instant('common.SUCCESS_REMINDER', {
      name: this.referralName
    });
    this._messageService.success(translatedText);
    this.reminderSent.emit();
    this._modal.close({ reloadList: true });
  }
}
